import React from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

function PrivacyPolicy() {
  const history = useHistory();
  const bg = useColorModeValue("white", "navy.900");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const headingColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box bg={bg} minH="100vh" py={{ base: "40px", md: "60px" }}>
      <Box maxW="860px" mx="auto" px={{ base: "24px", md: "40px" }}>

        {/* Back button */}
        <Button
          variant="outline"
          colorScheme="brand"
          fontWeight="light"
          size="sm"
          mb="32px"
          onClick={() => history.push("/auth/login")}
        >
          &larr; Torna al Login
        </Button>

        {/* Header */}
        <Heading color={headingColor} fontSize={{ base: "28px", md: "36px" }} mb="8px">
          Informativa sulla Privacy
        </Heading>
        <Text color={mutedColor} fontSize="sm" mb="8px">
          Ultimo aggiornamento: marzo 2026
        </Text>
        <Divider borderColor={borderColor} mb="32px" />

        {/* Intro */}
        <Section heading={null} color={textColor} headingColor={headingColor}>
          La presente Informativa sulla Privacy descrive come <strong>White River Srl</strong>
          (&ldquo;noi&rdquo;, &ldquo;nostro&rdquo;), societ&agrave; operante nella gestione di
          patrimoni immobiliari con sede in Italia, raccoglie, utilizza e protegge i dati personali
          degli utenti della nostra piattaforma gestionale interna (la &ldquo;Piattaforma&rdquo;).
          Trattiamo i dati personali nel rispetto del Regolamento Generale sulla Protezione dei Dati
          (GDPR) &mdash; Regolamento (UE) 2016/679 &mdash; e della normativa italiana applicabile in
          materia di protezione dei dati.
        </Section>

        <Section heading="1. Titolare del Trattamento" color={textColor} headingColor={headingColor}>
          Il Titolare del Trattamento &egrave;:
          <br /><br />
          <strong>White River Srl</strong><br />
          Settore di attivit&agrave;: gestione di patrimoni immobiliari<br />
          Paese: Italia<br />
          Email:{" "}
          <Text as="span" color="brand.500">
            info@whiteriver.it
          </Text>
        </Section>

        <Section heading="2. Quali Dati Raccogliamo" color={textColor} headingColor={headingColor}>
          Raccogliamo esclusivamente i dati personali necessari al funzionamento della Piattaforma, ossia:
          <br /><br />
          <BulletList items={[
            "Credenziali dell'account: indirizzo email e password cifrata (hash) utilizzati per autenticare gli utenti.",
            "Dati di profilo: nome e ruolo assegnato all'interno della Piattaforma (es. amministratore, ufficio, operazioni, manutenzione, pulizie).",
            "Dati operativi: informazioni sugli immobili, registrazioni delle prenotazioni, dati sugli ordini di biancheria, informazioni di magazzino e altri dati di gestione inseriti dagli utenti autorizzati.",
            "Dati di utilizzo: log di accesso, marche temporali e indirizzi IP registrati automaticamente per finalità di sicurezza e audit.",
          ]} color={textColor} />
          Non raccogliamo dati personali sensibili (categorie particolari ai sensi dell&apos;art. 9 GDPR)
          n&eacute; dati relativi a minori di 18 anni. La Piattaforma &egrave; accessibile esclusivamente
          a dipendenti e collaboratori autorizzati di White River Srl.
        </Section>

        <Section heading="3. Finalità e Base Giuridica del Trattamento" color={textColor} headingColor={headingColor}>
          <TableLike
            rows={[
              {
                purpose: "Autenticazione degli utenti e controllo degli accessi",
                basis: "Legittimo interesse (art. 6, par. 1, lett. f GDPR) — garantire la sicurezza della Piattaforma e limitarne l'accesso al personale autorizzato.",
              },
              {
                purpose: "Operazioni di gestione immobiliare e patrimoniale",
                basis: "Esecuzione di un contratto / legittimo interesse (art. 6, par. 1, lett. b e f GDPR) — consentire al personale autorizzato di gestire il patrimonio immobiliare.",
              },
              {
                purpose: "Gestione della biancheria e del magazzino",
                basis: "Legittimo interesse (art. 6, par. 1, lett. f GDPR) — supportare i flussi operativi interni.",
              },
              {
                purpose: "Registrazione degli eventi di sicurezza e tracciabilità",
                basis: "Obbligo legale e legittimo interesse (art. 6, par. 1, lett. c e f GDPR) — mantenere l'integrità e la tracciabilità del sistema.",
              },
              {
                purpose: "Comunicazioni agli utenti relative al loro account",
                basis: "Legittimo interesse (art. 6, par. 1, lett. f GDPR) — informare gli utenti in merito al reset della password e ad altre questioni relative all'account.",
              },
            ]}
            borderColor={borderColor}
            textColor={textColor}
            headingColor={headingColor}
          />
        </Section>

        <Section heading="4. Come Utilizziamo i Tuoi Dati" color={textColor} headingColor={headingColor}>
          I tuoi dati sono utilizzati esclusivamente per:
          <BulletList items={[
            "Autenticare e autorizzare il tuo accesso alla Piattaforma.",
            "Consentirti di svolgere il ruolo e le responsabilità assegnate all'interno della Piattaforma.",
            "Conservare le registrazioni operative relative alla gestione del patrimonio immobiliare.",
            "Garantire la sicurezza, l'integrità e la disponibilità della Piattaforma.",
            "Inviare email transazionali (es. link per il reset della password) su tua richiesta.",
          ]} color={textColor} />
        </Section>

        <Section heading="5. Comunicazione e Diffusione dei Dati" color={textColor} headingColor={headingColor}>
          Non vendiamo, non cediamo e non condividiamo i tuoi dati personali con terzi per finalità di
          marketing. I dati possono essere comunicati soltanto nei seguenti casi:
          <BulletList items={[
            "Fornitori di servizi: fornitori terzi di fiducia che ci assistono nell'hosting, nella manutenzione o nel funzionamento tecnico della Piattaforma, in qualità di Responsabili del trattamento sulla base di accordi scritti conformi all'art. 28 GDPR.",
            "Obblighi di legge: quando richiesto dalla legge, da un provvedimento dell'autorità giudiziaria o dalle autorità italiane o europee competenti.",
            "Operazioni societarie: in caso di fusione, acquisizione o cessione di ramo d'azienda, con adeguate garanzie.",
          ]} color={textColor} />
        </Section>

        <Section heading="6. Conservazione dei Dati" color={textColor} headingColor={headingColor}>
          Conserviamo i dati personali solo per il tempo necessario a conseguire le finalità descritte
          nella presente Informativa:
          <BulletList items={[
            "I dati dell'account sono conservati per tutta la durata del rapporto con White River Srl ed eliminati entro 30 giorni dalla disattivazione dell'account, salvo un periodo di conservazione più lungo previsto per legge.",
            "Le registrazioni operative e di gestione del patrimonio possono essere conservate fino a 10 anni per adempiere agli obblighi civilistici, fiscali e regolamentari previsti dalla legge italiana.",
            "I log di sicurezza e di accesso sono conservati fino a 12 mesi.",
          ]} color={textColor} />
        </Section>

        <Section heading="7. Sicurezza dei Dati" color={textColor} headingColor={headingColor}>
          Adottiamo misure tecniche e organizzative adeguate per proteggere i tuoi dati personali da
          accessi, divulgazioni, alterazioni o distruzioni non autorizzati. Tra queste:
          <BulletList items={[
            "Cifratura delle password (hashing) con algoritmi conformi agli standard di settore.",
            "Autenticazione basata su token (Laravel Sanctum) con sessioni a scadenza.",
            "Cifratura HTTPS per tutti i dati in transito.",
            "Controlli di accesso basati sui ruoli, che limitano la visibilità dei dati al solo personale autorizzato.",
            "Revisioni periodiche della sicurezza e audit degli accessi.",
          ]} color={textColor} />
          Nessun metodo di trasmissione su Internet &egrave; sicuro al 100%. In caso di violazione dei
          dati personali, provvederemo a notificarlo all&apos;autorit&agrave; di controllo competente
          (Garante per la Protezione dei Dati Personali) e agli interessati, come previsto dagli
          artt. 33&ndash;34 GDPR.
        </Section>

        <Section heading="8. I Tuoi Diritti ai Sensi del GDPR" color={textColor} headingColor={headingColor}>
          In qualit&agrave; di interessato ai sensi del GDPR, hai i seguenti diritti:
          <BulletList items={[
            "Diritto di accesso (art. 15): ottenere conferma che sia in corso un trattamento dei tuoi dati e riceverne copia.",
            "Diritto di rettifica (art. 16): chiedere la correzione di dati inesatti o incompleti.",
            "Diritto alla cancellazione (art. 17): chiedere la cancellazione dei tuoi dati quando non più necessari o trattati illecitamente.",
            "Diritto di limitazione (art. 18): chiedere la limitazione del trattamento in determinate circostanze.",
            "Diritto alla portabilità (art. 20): ricevere i tuoi dati in un formato strutturato e leggibile da dispositivo automatico.",
            "Diritto di opposizione (art. 21): opporti al trattamento fondato sul legittimo interesse.",
            "Diritto di reclamo: presentare reclamo all'autorità italiana di protezione dei dati — Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).",
          ]} color={textColor} />
          Per esercitare uno qualsiasi dei diritti sopra indicati, scrivici a{" "}
          <Text as="span" color="brand.500">info@whiteriver.it</Text>. Risponderemo entro 30 giorni,
          come previsto dal GDPR.
        </Section>

        <Section heading="9. Cookie e Tracciamento" color={textColor} headingColor={headingColor}>
          La Piattaforma utilizza esclusivamente meccanismi tecnici essenziali (come i token di
          autenticazione memorizzati nel local storage del browser) necessari al suo funzionamento.
          Non utilizziamo cookie pubblicitari, pixel di tracciamento o strumenti di analisi di terze parti.
        </Section>

        <Section heading="10. Trasferimenti Internazionali" color={textColor} headingColor={headingColor}>
          I tuoi dati sono conservati e trattati all&apos;interno dello Spazio Economico Europeo (SEE).
          Qualora si rendesse necessario un trasferimento al di fuori del SEE, garantiremo l&apos;adozione
          di adeguate garanzie (ad esempio le Clausole Contrattuali Standard), in conformit&agrave; al
          Capo V del GDPR.
        </Section>

        <Section heading="11. Modifiche alla Presente Informativa" color={textColor} headingColor={headingColor}>
          Potremmo aggiornare periodicamente la presente Informativa sulla Privacy per riflettere
          cambiamenti nelle nostre prassi o negli obblighi normativi. La data di
          &ldquo;Ultimo aggiornamento&rdquo; riportata in cima a questa pagina indica quando &egrave;
          stata effettuata l&apos;ultima revisione. L&apos;uso continuato della Piattaforma dopo un
          aggiornamento costituisce accettazione dell&apos;Informativa aggiornata.
        </Section>

        <Section heading="12. Contatti" color={textColor} headingColor={headingColor}>
          Per qualsiasi domanda, richiesta o segnalazione relativa alla presente Informativa sulla
          Privacy o al trattamento dei tuoi dati personali, contatta:
          <br /><br />
          <strong>White River Srl</strong><br />
          Email:{" "}
          <Text as="span" color="brand.500">info@whiteriver.it</Text>
        </Section>

        {/* Footer */}
        <Divider borderColor={borderColor} mt="40px" mb="24px" />
        <Flex justifyContent="space-between" alignItems="center" pb="40px">
          <Text color={mutedColor} fontSize="sm">
            &copy; {new Date().getFullYear()} White River Srl. Tutti i diritti riservati.
          </Text>
          <Button
            variant="outline"
            colorScheme="brand"
            fontWeight="light"
            size="sm"
            onClick={() => history.push("/auth/login")}
          >
            Torna al Login
          </Button>
        </Flex>

      </Box>
    </Box>
  );
}

/* ── helpers ── */

function Section({ heading, children, color, headingColor }) {
  return (
    <Box mb="28px">
      {heading && (
        <Heading color={headingColor} fontSize="18px" fontWeight="600" mb="12px">
          {heading}
        </Heading>
      )}
      <Text color={color} fontSize="sm" lineHeight="1.8">
        {children}
      </Text>
    </Box>
  );
}

function BulletList({ items, color }) {
  return (
    <Box as="ul" pl="20px" mt="10px" mb="6px">
      {items.map((item, i) => (
        <Box as="li" key={i} mb="8px">
          <Text color={color} fontSize="sm" lineHeight="1.8">
            {item}
          </Text>
        </Box>
      ))}
    </Box>
  );
}

function TableLike({ rows, borderColor, textColor, headingColor }) {
  return (
    <Box border="1px solid" borderColor={borderColor} borderRadius="8px" overflow="hidden" mt="12px">
      {/* header */}
      <Flex bg="brand.500" px="16px" py="10px">
        <Text flex="1" color="white" fontSize="xs" fontWeight="600" textTransform="uppercase">
          Finalit&agrave;
        </Text>
        <Text flex="2" color="white" fontSize="xs" fontWeight="600" textTransform="uppercase">
          Base Giuridica
        </Text>
      </Flex>
      {rows.map((row, i) => (
        <Flex
          key={i}
          px="16px"
          py="12px"
          borderTop={i > 0 ? "1px solid" : "none"}
          borderColor={borderColor}
          flexDirection={{ base: "column", md: "row" }}
          gap={{ base: "4px", md: "0" }}
        >
          <Text flex="1" color={headingColor} fontSize="sm" fontWeight="500" pr={{ md: "16px" }}>
            {row.purpose}
          </Text>
          <Text flex="2" color={textColor} fontSize="sm" lineHeight="1.7">
            {row.basis}
          </Text>
        </Flex>
      ))}
    </Box>
  );
}

export default PrivacyPolicy;
